import { useEffect, useState, type ReactNode } from 'react';
import type { BookMeta } from '@shared/types';
import { getCover } from '../covers';

interface Props {
  book: BookMeta;
  // Whether the file is readable locally (so we can generate a real cover).
  available: boolean;
  progressPct?: number;
  children?: ReactNode; // action area (Read / Download / …)
}

// A library tile: cover (real thumbnail when local, else a styled placeholder),
// title, author, optional progress, and a contextual action row.
export function BookCard({ book, available, progressPct, children }: Props) {
  const [cover, setCover] = useState<string | null>(null);

  useEffect(() => {
    if (!available) {
      setCover(null);
      return;
    }
    let alive = true;
    getCover({ id: book.id, title: book.title, type: book.type })
      .then((url) => alive && setCover(url))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [book.id, book.title, book.type, available]);

  const pct = progressPct != null ? Math.round(progressPct) : undefined;

  return (
    <div className="bookcard">
      <div className="cover" data-type={book.type}>
        {cover ? (
          <img src={cover} alt="" className="cover-img" loading="lazy" />
        ) : (
          <div className="cover-ph">
            <span className="cover-ph-title">{book.title}</span>
            {book.author && <span className="cover-ph-author">{book.author}</span>}
            <span className="cover-ph-type">{book.type}</span>
          </div>
        )}
        {pct != null && (
          <div className="cover-prog" title={`${pct}%`}>
            <div className="cover-prog-fill" style={{ width: `${pct}%` }} />
          </div>
        )}
      </div>
      <div className="bookcard-meta">
        <span className="booktitle" title={book.title}>{book.title}</span>
        <span className="muted bookauthor">{book.author || book.type}</span>
      </div>
      {children && <div className="bookcard-actions">{children}</div>}
    </div>
  );
}
